import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export function GoodsDetailQuickSelectMenu({
  totalCount = 0,
  onSelect,
  triggerClassName = 'ipe-gallery-selection-bar__action',
  placement = 'up',
}) {
  const { t } = useTranslation('workbench');
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 168);
    const left = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - minWidth - 8),
    );
    if (placement === 'up') {
      setMenuStyle({
        position: 'fixed',
        left,
        bottom: Math.max(8, window.innerHeight - rect.top + 6),
        top: 'auto',
        minWidth,
        zIndex: 10020,
      });
      return;
    }
    setMenuStyle({
      position: 'fixed',
      left,
      top: rect.bottom + 6,
      bottom: 'auto',
      minWidth,
      zIndex: 10020,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, placement, totalCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleSelect = (value) => {
    setOpen(false);
    onSelect?.(value);
  };

  const dropdown = open && menuStyle ? (
    <div
      ref={menuRef}
      className="ai-image-detail-download-menu__dropdown goods-detail__quick-select-dropdown"
      role="menu"
      aria-label={t('goodsDetailQuickSelect.aria')}
      style={menuStyle}
    >
      <button
        type="button"
        role="menuitem"
        className="ai-image-detail-download-menu__item"
        onClick={() => handleSelect('all')}
      >
        {t('studioChrome.selectAll')}
      </button>
      {Array.from({ length: totalCount }, (_, index) => (
        <button
          key={index}
          type="button"
          role="menuitem"
          className="ai-image-detail-download-menu__item"
          onClick={() => handleSelect(`position-${index + 1}`)}
        >
          {t('goodsDetail.quickSelectPosition', { index: index + 1 })}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`ai-image-detail-download-menu goods-detail__quick-select-menu${
        open ? ' ai-image-detail-download-menu--open' : ''
      }${placement === 'up' ? ' ai-image-detail-download-menu--up' : ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`${triggerClassName} ai-image-detail-download-menu__trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{t('goodsDetailQuickSelect.aria')}</span>
        <ChevronDown size={14} className="ai-image-detail-download-menu__chevron" aria-hidden="true" />
      </button>
      {dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
}

export default GoodsDetailQuickSelectMenu;
